import { Plus } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useT } from "@/i18n/provider"
import { api, ensureActiveWorkspace, toastApiError } from "@/lib/api"
import type { Workspace } from "@/lib/types"
import { getActiveWorkspaceId, setActiveWorkspaceId } from "@/lib/workspace"

/** Lists the caller's workspaces and lets them switch the active one, or create
 *  a new one via the "+" button.
 *  Default resolution (which workspace is "active" on first load) happens
 *  once at app bootstrap, in auth.tsx - see ensureActiveWorkspace in api.ts.
 *  This component only reads that choice and calls ensureActiveWorkspace
 *  defensively in case it hasn't resolved yet (idempotent no-op otherwise);
 *  it does not own the resolution itself. Every request already carries
 *  X-Workspace-Id once it is set (see api.ts's authHeaders); switching (and
 *  creating, which switches to the new workspace) reloads so all
 *  already-fetched data (agents, runs, ...) refetches under the new
 *  workspace, matching how the rest of the app refetches after a
 *  state-changing action. */
export function WorkspaceSwitcher() {
  const t = useT()
  const [workspaces, setWorkspaces] = React.useState<Workspace[] | null>(null)
  const [activeId, setActiveId] = React.useState<string | null>(getActiveWorkspaceId())
  const [dialogOpen, setDialogOpen] = React.useState(false)

  React.useEffect(() => {
    ensureActiveWorkspace()
      .then(() => api.listWorkspaces())
      .then((list) => {
        setWorkspaces(list)
        setActiveId(getActiveWorkspaceId())
      })
      .catch((error) => toastApiError(error, t("workspace.loadFailed")))
  }, [t])

  function onChange(id: string) {
    if (id === activeId) return
    setActiveWorkspaceId(id)
    setActiveId(id)
    window.location.reload()
  }

  if (!workspaces || workspaces.length === 0) return null

  const current = workspaces.find((workspace) => workspace.id === activeId) ?? workspaces[0]

  return (
    <div className="flex items-center gap-1">
      <Select value={current.id} onValueChange={onChange}>
        <SelectTrigger aria-label={t("workspace.switcherLabel")} className="h-8 min-w-0 flex-1 text-sm">
          <SelectValue>{current.name}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {workspaces.map((workspace) => (
            <SelectItem key={workspace.id} value={workspace.id}>
              {workspace.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="outline"
        size="icon"
        className="size-8 shrink-0"
        aria-label={t("workspace.newLabel")}
        title={t("workspace.new")}
        onClick={() => setDialogOpen(true)}
      >
        <Plus className="size-4" />
      </Button>
      <CreateWorkspaceDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  )
}

/** Creates a workspace, then makes it active and reloads so the whole app
 *  refetches under it - the same switch path onChange takes above. */
function CreateWorkspaceDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const t = useT()
  const [name, setName] = React.useState("")
  const [busy, setBusy] = React.useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!name.trim()) return
    setBusy(true)
    try {
      const workspace = await api.createWorkspace(name.trim())
      toast.success(t("workspace.toastCreated"))
      setName("")
      setActiveWorkspaceId(workspace.id)
      window.location.reload()
    } catch (error) {
      toastApiError(error, t("workspace.createFailed"))
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("workspace.new")}</DialogTitle>
          <DialogDescription>{t("workspace.dialogDescription")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-workspace-name">{t("common.name")}</Label>
            <Input
              id="new-workspace-name"
              required
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("workspace.namePlaceholder")}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={busy || !name.trim()}>
              {t("workspace.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
